import { _ } from '@ngx-translate/core';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import {
  ldEventInvestigationResultIcon,
  ldEventStatusIcon,
} from '@app/main/leak-detection/configs/ld-event-status.config';
import {
  DialogEventCommentRequirement,
  DialogEventStatusAction,
  DialogEventStatusActionForm,
  DialogEventStatusActionGroup,
} from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-status-action.enum';
import {
  DIALOG_EVENT_REASON_WORDING,
  IDialogEventCommentWording,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-comment.config';
import { ldEventInvestigationResultLocalize } from '@app/main/leak-detection/localize/ld-event-status';

export interface IDialogEventStatusTransition {
  action: DialogEventStatusAction;
  target: LdEventStatus;
  investigationResult: LdEventInvestigationResult | null;
  form: DialogEventStatusActionForm;
  comment?: DialogEventCommentRequirement;
  commentWording?: IDialogEventCommentWording;
  instant?: boolean;
  group?: DialogEventStatusActionGroup;
  label: string;
  confirmLabel: string;
  icon: string;
  separatorBefore?: boolean;
}

const transitions: IDialogEventStatusTransition[] = [
  {
    action: DialogEventStatusAction.MoveToMonitoring,
    target: LdEventStatus.Monitoring,
    investigationResult: null,
    form: DialogEventStatusActionForm.None,
    instant: true,
    label: _(`@@fp-event-status-action__move-to-monitoring`),
    confirmLabel: _(`@@fp-event-status-action-confirm__move-to-monitoring`),
    icon: ldEventStatusIcon[LdEventStatus.Monitoring],
  },
  {
    action: DialogEventStatusAction.MoveToInvestigation,
    target: LdEventStatus.Investigation,
    investigationResult: null,
    form: DialogEventStatusActionForm.Assignee,
    comment: DialogEventCommentRequirement.Optional,
    label: _(`@@fp-event-status-action__move-to-investigation`),
    confirmLabel: _(`@@fp-event-status-action-confirm__move-to-investigation`),
    icon: ldEventStatusIcon[LdEventStatus.Investigation],
  },
  {
    action: DialogEventStatusAction.ReturnToNew,
    target: LdEventStatus.New,
    investigationResult: null,
    form: DialogEventStatusActionForm.None,
    instant: true,
    label: _(`@@fp-event-status-action__return-to-new`),
    confirmLabel: _(`@@fp-event-status-action-confirm__return-to-new`),
    icon: ldEventStatusIcon[LdEventStatus.New],
  },
  {
    action: DialogEventStatusAction.StartInvestigation,
    target: LdEventStatus.Investigation,
    investigationResult: null,
    form: DialogEventStatusActionForm.Assignee,
    comment: DialogEventCommentRequirement.Optional,
    label: _(`@@fp-event-status-action__start-investigation`),
    confirmLabel: _(`@@fp-event-status-action-confirm__start-investigation`),
    icon: ldEventStatusIcon[LdEventStatus.Investigation],
  },
  {
    action: DialogEventStatusAction.ReturnToMonitoring,
    target: LdEventStatus.Monitoring,
    investigationResult: null,
    form: DialogEventStatusActionForm.None,
    comment: DialogEventCommentRequirement.Required,
    commentWording: DIALOG_EVENT_REASON_WORDING,
    label: _(`@@fp-event-status-action__return-to-monitoring`),
    confirmLabel: _(`@@fp-event-status-action-confirm__return-to-monitoring`),
    icon: ldEventStatusIcon[LdEventStatus.Monitoring],
  },
  {
    action: DialogEventStatusAction.ResolveAsLeak,
    target: LdEventStatus.Resolved,
    investigationResult: LdEventInvestigationResult.Leak,
    form: DialogEventStatusActionForm.Resolution,
    comment: DialogEventCommentRequirement.Optional,
    group: DialogEventStatusActionGroup.Resolve,
    label: ldEventInvestigationResultLocalize[LdEventInvestigationResult.Leak],
    confirmLabel: _(`@@fp-event-status-action-confirm__resolve-as-leak`),
    icon: ldEventInvestigationResultIcon[LdEventInvestigationResult.Leak],
  },
  {
    action: DialogEventStatusAction.ResolveAsCustomerSideLeak,
    target: LdEventStatus.Resolved,
    investigationResult: LdEventInvestigationResult.CustomerSideLeak,
    form: DialogEventStatusActionForm.Resolution,
    comment: DialogEventCommentRequirement.Optional,
    group: DialogEventStatusActionGroup.Resolve,
    label: ldEventInvestigationResultLocalize[LdEventInvestigationResult.CustomerSideLeak],
    confirmLabel: _(`@@fp-event-status-action-confirm__resolve-as-customer-side-leak`),
    icon: ldEventInvestigationResultIcon[LdEventInvestigationResult.CustomerSideLeak],
  },
  {
    action: DialogEventStatusAction.ResolveAsNoLeak,
    target: LdEventStatus.Resolved,
    investigationResult: LdEventInvestigationResult.NoLeak,
    form: DialogEventStatusActionForm.Resolution,
    comment: DialogEventCommentRequirement.Optional,
    group: DialogEventStatusActionGroup.Resolve,
    label: ldEventInvestigationResultLocalize[LdEventInvestigationResult.NoLeak],
    confirmLabel: _(`@@fp-event-status-action-confirm__resolve-as-no-leak`),
    icon: ldEventInvestigationResultIcon[LdEventInvestigationResult.NoLeak],
  },
  {
    action: DialogEventStatusAction.ReopenInvestigation,
    target: LdEventStatus.Investigation,
    investigationResult: null,
    form: DialogEventStatusActionForm.None,
    comment: DialogEventCommentRequirement.Required,
    commentWording: DIALOG_EVENT_REASON_WORDING,
    label: _(`@@fp-event-status-action__reopen-investigation`),
    confirmLabel: _(`@@fp-event-status-action-confirm__reopen-investigation`),
    icon: ldEventStatusIcon[LdEventStatus.Investigation],
  },
];

export const dialogEventStatusTransitions: Record<DialogEventStatusAction, IDialogEventStatusTransition> =
  transitions.reduce(
    (map, transition) => ({ ...map, [transition.action]: transition }),
    {} as Record<DialogEventStatusAction, IDialogEventStatusTransition>
  );

/** what the `Change status` drawer offers from each status, in the order the design lists it */
export const dialogEventStatusActions: Record<LdEventStatus, DialogEventStatusAction[]> = {
  [LdEventStatus.New]: [DialogEventStatusAction.MoveToMonitoring, DialogEventStatusAction.MoveToInvestigation],
  [LdEventStatus.Monitoring]: [DialogEventStatusAction.ReturnToNew, DialogEventStatusAction.StartInvestigation],
  [LdEventStatus.Investigation]: [
    DialogEventStatusAction.ResolveAsLeak,
    DialogEventStatusAction.ResolveAsCustomerSideLeak,
    DialogEventStatusAction.ResolveAsNoLeak,
    DialogEventStatusAction.ReturnToMonitoring,
  ],
  [LdEventStatus.Resolved]: [DialogEventStatusAction.ReopenInvestigation],
};

/** the step order of the pipeline indicator */
export const dialogEventPipelineStages: LdEventStatus[] = [
  LdEventStatus.New,
  LdEventStatus.Monitoring,
  LdEventStatus.Investigation,
  LdEventStatus.Resolved,
];

/** what a group of transitions is announced with, above the branch that lists them */
export const dialogEventStatusActionGroups: Record<DialogEventStatusActionGroup, { label: string; icon: string }> = {
  [DialogEventStatusActionGroup.Resolve]: {
    label: _(`@@fp-event-status-action-group__resolve`),
    icon: ldEventStatusIcon[LdEventStatus.Resolved],
  },
};

/** `Return to monitoring` sits below the resolutions, behind a rule */
export const dialogEventSeparatedActions: ReadonlySet<DialogEventStatusAction> = new Set([
  DialogEventStatusAction.ReturnToMonitoring,
]);
